import type { GenericPageData } from '@cng/shared';
import { useSite } from '@/app/page-data';
import { SectionContainer } from '@/components/layout/SectionContainer';
import { SectionRenderer } from '@/components/sections/SectionRenderer';

/**
 * Renders any CMS-defined page — the SEO landing pages, legal pages and
 * campaign pages all come through here.
 *
 * Those pages were hand-authored HTML with their own stylesheet on the old
 * site, so both are preserved: the markup lives in a `richText` section and
 * the CSS on the page, already scoped by the server to `[data-page="<slug>"]`.
 * That attribute sits on the migrated block itself rather than on this
 * wrapper, because the old stylesheets carry a `* { margin: 0; padding: 0 }`
 * reset that would otherwise flatten the shared locations block and footer.
 */
/**
 * A page title read as a heading: the site name belongs in `<title>`, not in
 * the `<h1>` a screen reader announces.
 */
function headingFrom(title: string): string {
  return title.replace(/\s*[-|–—]\s*Charlie\s*(and|&)\s*Guys.*$/i, '').trim() || title;
}

export function GenericPage({ data }: { data: GenericPageData & { kind: 'page' } }) {
  const { settings, branches } = useSite();
  const page = data.page;
  const sections = [...(page.sections ?? [])].sort((a, b) => a.order - b.order);
  const scopeKey = page.slug || 'home';

  // A handful of migrated pages style their title as a plain <div>, so there is
  // no heading element to promote at import time. Rather than restyle the page,
  // give it a heading search engines and screen readers can use.
  const hasHeading = sections.some((s) => (
    (s.type === 'hero' && typeof s.data?.title === 'string' && s.data.title)
    || (typeof s.data?.html === 'string' && /<h1[\s>]/i.test(s.data.html))
  ));

  return (
    <article className="cng-generic-page">
      {page.customCss ? (
        // Already scoped and sanitised server-side; see server/src/utils/css-scope.ts
        <style dangerouslySetInnerHTML={{ __html: page.customCss }} />
      ) : null}

      {hasHeading || sections.length === 0 ? null : page.pageType === 'locations' ? (
        <header className="cng-location-heading">
          <h1>{headingFrom(page.title || page.seo?.title || '')}</h1>
        </header>
      ) : <h1 className="sr-only">{headingFrom(page.title || page.seo?.title || '')}</h1>}

      {sections.length === 0 ? (
        <SectionContainer>
          <div className="cng-section">
            <h1>{page.title}</h1>
          </div>
        </SectionContainer>
      ) : (
        sections.map((section) => (
          <SectionRenderer
            key={section.key}
            section={section}
            ctx={{ settings, branches, scopeKey }}
          />
        ))
      )}
    </article>
  );
}
